// src/components/onboarding/FoxOnboarding.jsx
// Chat-based onboarding for SurFox. Mounted at /onboarding (the legacy wizard
// stays available at /onboarding/legacy as a fallback). The conversation is
// driven by /api/onboarding/chat (Claude); the completion build sequence by
// /api/onboarding/finalize.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Send } from 'lucide-react';
import { fetchWithCsrf } from '../../lib/fetchWithCsrf';
import supabase from '../../lib/supabaseClient';

// Notes panel keys MUST match the JSON keys Claude returns under `extracted`.
const NOTES_FIELDS = [
  { key: 'company_name',        label: 'Company' },
  { key: 'industry',            label: 'Industry' },
  { key: 'what_they_sell',      label: 'What they sell' },
  { key: 'who_their_leads_are', label: 'Lead audience' },
  { key: 'website',             label: 'Website' },
  { key: 'area_code',           label: 'Area code' },
];

const BUILD_TASKS = [
  // "Knowledge template" — onboarding writes a master_kb row (skeleton or
  // scrape-driven), which is the editable starting template the user later
  // fills in from AI Studio. It is NOT a fully populated KB.
  { key: 'knowledgeBase', label: 'Knowledge template' },
  { key: 'phoneNumber',   label: 'Phone number' },
  { key: 'campaign',      label: 'Campaign' },
  { key: 'talkTracks',    label: 'Talk tracks' },
];

const CHAR_STREAM_MS = 22;
const TYPING_BEFORE_MS = 750;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Hardcoded first message. Identical for every user, displayed instantly
// after the welcome modal dismisses with no API call, no typing indicator,
// no streaming. Claude takes over starting with the user's first reply.
const HARDCODED_OPENER =
  "Hey, I'm Surf. Let's get your account set up. To kick things off, what's your company called?";

// ─── Root component ─────────────────────────────────────────────────────────
export default function FoxOnboarding() {
  const navigate = useNavigate();
  // Messages: { role, text, raw?, streaming? }
  //   user.text       — what the user typed
  //   assistant.text  — displayed text, revealed char-by-char while streaming
  //   assistant.raw   — the full JSON Claude returned (sent back as conversation
  //                     history so Claude carries forward its extracted state)
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isFoxTyping, setIsFoxTyping] = useState(false);
  const [foxState, setFoxState] = useState('idle'); // idle | thinking | speaking
  const [captures, setCaptures] = useState({});
  const [buildState, setBuildState] = useState({});
  const [completed, setCompleted] = useState(false);
  const [phoneRetryMode, setPhoneRetryMode] = useState(false);
  // Test mode: when a global_admin loads this route, bypass the
  // onboarding-complete redirect. When complete=true fires we pause for an
  // admin review overlay instead of either auto-finalizing OR auto-resetting.
  // The admin chooses: write to their tenant (real finalize) or discard.
  const [testMode, setTestMode] = useState(false);
  // When non-null, the admin review overlay is showing this extracted object.
  const [pendingTestExtracted, setPendingTestExtracted] = useState(null);
  const [guardChecked, setGuardChecked] = useState(false);
  // Welcome modal: shown after the guard check clears, before the first chat
  // turn fires. The conversation (and the first API call) only starts after
  // the user clicks "Let's go".
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  // Captured during the guard check so the KB realtime sub in runFinalize
  // doesn't need to re-fetch the profile.
  const [tenantId, setTenantId] = useState(null);

  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const startedRef = useRef(false);
  // Guards against double-firing handleTestWrite if the admin clicks twice.
  const writeInFlightRef = useRef(false);
  // KB realtime watch — held in refs so runFinalize can register them and
  // the unmount cleanup can tear them down without racing.
  const kbChannelRef = useRef(null);
  const kbTimeoutRef = useRef(null);
  // Stamps the moment Surf opens the conversation. Sent to /finalize as
  // started_at so the persisted transcript carries an accurate duration
  // signal (completed_at - started_at = how long onboarding took).
  const startedAtRef = useRef(null);
  // Mirror of the messages state. callFinalizeApi runs inside async chains
  // whose closures captured `messages` from an earlier render; without a
  // ref the persisted transcript would miss every assistant streamAssistant
  // append that happened after the closure was created. The useEffect
  // below keeps the ref in lockstep with state.
  const messagesRef = useRef([]);

  const canSend = foxState === 'idle' && !isFoxTyping && !completed && !pendingTestExtracted;

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
    messagesRef.current = messages;
  }, [messages, isFoxTyping]);

  useEffect(() => {
    if (canSend && inputRef.current) inputRef.current.focus();
  }, [canSend]);

  // KB realtime channel + safety-timeout cleanup on unmount. runFinalize may
  // register either or both; tear them down here so a user who navigates
  // away mid-finalize doesn't leak a websocket or stray timer.
  useEffect(() => {
    return () => {
      if (kbChannelRef.current) {
        supabase.removeChannel(kbChannelRef.current);
        kbChannelRef.current = null;
      }
      if (kbTimeoutRef.current) {
        clearTimeout(kbTimeoutRef.current);
        kbTimeoutRef.current = null;
      }
    };
  }, []);

  // Guard check on mount: look up the user's role + tenant onboarding state.
  // - global_admin: enable testMode, always run the conversation, never redirect
  // - any other role with onboarding_complete=true: redirect to /dashboard
  // - everyone else: proceed normally
  useEffect(() => {
    let cancelled = false;
    async function checkGuard() {
      try {
        const { data: { user }, error: userErr } = await supabase.auth.getUser();
        if (cancelled) return;
        if (userErr || !user) {
          // No authenticated user. Let the conversation proceed; the API
          // layer will reject calls that need auth.
          setGuardChecked(true);
          return;
        }

        const { data: profile } = await supabase
          .from('users_profile')
          .select('tenant_id, role')
          .eq('id', user.id)
          .single();
        if (cancelled) return;

        // Stash tenant_id for the KB realtime watch in runFinalize. Both
        // regular users and admins in testMode rely on this.
        if (profile?.tenant_id) setTenantId(profile.tenant_id);

        const isAdmin = profile?.role === 'global_admin';
        if (isAdmin) {
          setTestMode(true);
          setGuardChecked(true);
          return;
        }

        if (profile?.tenant_id) {
          const { data: tenant } = await supabase
            .from('tenants')
            .select('onboarding_complete')
            .eq('id', profile.tenant_id)
            .single();
          if (cancelled) return;
          if (tenant?.onboarding_complete) {
            navigate('/dashboard', { replace: true });
            return;
          }
        }

        setGuardChecked(true);
      } catch (e) {
        console.error('Onboarding guard check failed', e);
        if (!cancelled) setGuardChecked(true); // proceed conservatively
      }
    }
    checkGuard();
    return () => { cancelled = true; };
  }, [navigate]);

  // After the welcome modal dismisses, display the hardcoded opener instantly.
  // No /api/onboarding/chat call fires until the user types their first reply.
  useEffect(() => {
    if (!guardChecked) return;
    if (!welcomeDismissed) return;
    if (startedRef.current) return;
    startedRef.current = true;
    startedAtRef.current = new Date().toISOString();
    setMessages([{ role: 'assistant', text: HARDCODED_OPENER, raw: null, streaming: false }]);
  }, [guardChecked, welcomeDismissed]);

  // ─── Streaming helper ───────────────────────────────────────────────────
  // opts.speakingState — what foxState becomes while the bubble streams in.
  //   Defaults to 'speaking'. The retry path uses 'distracted' so the amber
  //   glow + head tilt persist through the squirrel line.
  // opts.endState — what foxState becomes after the stream completes.
  //   Defaults to 'idle'.
  async function streamAssistant(text, raw = null, opts = {}) {
    const { speakingState = 'speaking', endState = 'idle' } = opts;
    setFoxState('thinking');
    setIsFoxTyping(true);
    await sleep(TYPING_BEFORE_MS);
    setIsFoxTyping(false);
    setFoxState(speakingState);
    setMessages((prev) => [...prev, { role: 'assistant', text: '', raw, streaming: true }]);
    for (let i = 1; i <= text.length; i++) {
      await sleep(CHAR_STREAM_MS);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], text: text.slice(0, i) };
        return next;
      });
    }
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], streaming: false };
      return next;
    });
    setFoxState(endState);
  }

  // ─── API helpers ─────────────────────────────────────────────────────────
  async function callChatApi(historyForApi) {
    const baseMessages = historyForApi.map((m) => ({
      role: m.role,
      content: m.role === 'assistant' ? (m.raw || m.text) : m.text,
    }));
    // The hardcoded opener is the first assistant turn in our local history.
    // The Anthropic Messages API requires user-first turns, so prepend a
    // synthetic kickoff whenever the history starts with assistant.
    const apiMessages = baseMessages.length > 0 && baseMessages[0].role === 'assistant'
      ? [{ role: 'user', content: 'Begin onboarding.' }, ...baseMessages]
      : baseMessages;
    const res = await fetchWithCsrf('/api/onboarding/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: apiMessages }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`Chat failed: ${res.status} ${detail.slice(0, 200)}`);
    }
    return res.json();
  }

  async function callFinalizeApi(extracted) {
    // Bundle the conversation transcript for server-side persistence. Strip
    // the ephemeral `streaming` UI flag; keep `raw` (the full per-turn JSON
    // Claude returned) so /finalize can replay how extracted state evolved.
    // Phone-retry causes runFinalize to fire multiple times — only the
    // attempt that succeeds past step 5 (phone purchase) reaches the
    // transcript insert at step 10. Pathological case: success past step 5
    // but failure at step 10 with subsequent admin retry would write a
    // duplicate row; sweep these later with a SELECT on tenant_id +
    // started_at if it becomes a problem (rare enough to defer).
    const transcriptMessages = (messagesRef.current || []).map((m) => ({
      role: m.role,
      text: m.text,
      raw: m.raw || null,
    }));
    const res = await fetchWithCsrf('/api/onboarding/finalize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        extracted,
        messages: transcriptMessages,
        started_at: startedAtRef.current || null,
      }),
    });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body };
  }

  // ─── One chat turn ──────────────────────────────────────────────────────
  async function runChatTurn(messageHistory) {
    // Show the typing indicator and shift the fox into 'thinking' state
    // BEFORE the API call fires. Previously the indicator only appeared
    // during streamAssistant's artificial 750ms pre-stream pause AFTER the
    // API returned, so any /chat call taking >200ms left the conversation
    // visually frozen until Claude responded. Setting these up front means
    // the user always sees Surf reacting the instant they hit send.
    setFoxState('thinking');
    setIsFoxTyping(true);

    let chatResp;
    try {
      chatResp = await callChatApi(messageHistory);
    } catch (err) {
      console.error('chat failed, retrying once', err);
      // Fox-flavored retry. 'distracted' state holds amber glow + head tilt
      // through the line so the hiccup reads as character, not a bug. The
      // next successful streamAssistant call resets the fox to thinking →
      // speaking → idle on its own.
      await streamAssistant(
        "Surf got distracted by a squirrel. One sec...",
        null,
        { speakingState: 'distracted', endState: 'distracted' }
      );
      try {
        chatResp = await callChatApi(messageHistory);
      } catch (err2) {
        console.error('chat retry also failed', err2);
        await streamAssistant("Surf lost the trail. Try refreshing the page?");
        return;
      }
    }

    const { message, extracted, complete } = chatResp;
    await streamAssistant(message, JSON.stringify(chatResp));
    if (extracted) setCaptures(extracted);

    if (complete) {
      if (testMode) {
        // Global admin path: pause for review. The overlay shows a readable
        // summary of the extracted values; admin chooses to fire the real
        // finalize against their own tenant or discard and restart.
        console.log('[fox-onboarding test] complete=true, extracted:', JSON.stringify(extracted, null, 2));
        setPendingTestExtracted(extracted);
        return;
      }
      // Dwell beat: let the user finish reading Surf's closing message
      // before the right-panel build animation starts pulling their eye
      // away. Without this pause, the build checkmarks begin animating
      // the instant streamAssistant returns, which feels rushed.
      await sleep(2000);
      await runFinalize(extracted);
    }
  }

  // ─── Finalize: backend writes + phone provisioning ──────────────────────
  async function runFinalize(extracted) {
    // Visual progress while the API call runs.
    setBuildState((s) => ({ ...s, knowledgeBase: 'in_progress' }));
    await sleep(350);
    setBuildState((s) => ({ ...s, phoneNumber: 'in_progress' }));

    const result = await callFinalizeApi(extracted);

    if (!result.ok) {
      const code = result.body?.error;
      if (code === 'PHONE_AREA_CODE_UNAVAILABLE' || code === 'PHONE_PROVISIONING_FAILED') {
        // Recoverable: ask the user for a different area code.
        setBuildState({});
        setPhoneRetryMode(true);
        const msg = result.body?.message
          || `Hmm, couldn't get a number with area code ${extracted.area_code}. What's another area code you want to try?`;
        await streamAssistant(msg);
        return;
      }
      console.error('[fox-onboarding] finalize failed (non-recoverable)', {
        status: result.status,
        code: result.body?.error,
        step: result.body?.step,
        message: result.body?.message,
      });
      await streamAssistant("Something went wrong building your account, let me try once more.");
      const retry = await callFinalizeApi(extracted);
      if (!retry.ok) {
        console.error('[fox-onboarding] finalize retry also failed', {
          status: retry.status,
          code: retry.body?.error,
          step: retry.body?.step,
          message: retry.body?.message,
        });
        await streamAssistant("Still hitting an error. Please refresh or reach out to support.");
        setBuildState({});
        return;
      }
    }

    // Success: animate the remaining build tasks, then transition. The KB
    // scrape + Claude call runs server-side fire-and-forget (typically 10-20s)
    // so knowledgeBase stays "in_progress" while the other three flip to
    // "done", then we estimate completion via timeout. If the user has
    // already navigated to the dashboard the setState is a harmless no-op.
    setBuildState((s) => ({ ...s, phoneNumber: 'done' }));
    await sleep(400);
    setBuildState((s) => ({ ...s, campaign: 'in_progress' }));
    await sleep(800);
    setBuildState((s) => ({ ...s, campaign: 'done', talkTracks: 'in_progress' }));
    await sleep(900);
    setBuildState((s) => ({
      ...s,
      phoneNumber: 'done',
      campaign: 'done',
      talkTracks: 'done',
      // knowledgeBase intentionally left as 'in_progress'
    }));
    // KB realtime watch — flip "Knowledge template" to done when the
    // master_kb row lands with ingestion_status='complete'. generateOnboardingKb
    // is fire-and-forget from /finalize so we don't know exactly when it
    // finishes; subscribing beats guessing with a fixed timeout. A 25s
    // safety fallback flips it anyway in case realtime publication is
    // disabled for knowledge_base or the row never appears (e.g. server-side
    // crash). Channel + timer live in refs so the unmount cleanup wins races.
    if (tenantId && !kbChannelRef.current) {
      const channel = supabase
        .channel(`onboarding-kb-${tenantId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'knowledge_base',
            filter: `tenant_id=eq.${tenantId}`,
          },
          (payload) => {
            const row = payload.new;
            if (row?.source_type === 'master_kb' && row?.ingestion_status === 'complete') {
              setBuildState((s) => ({ ...s, knowledgeBase: 'done' }));
              if (kbChannelRef.current) {
                supabase.removeChannel(kbChannelRef.current);
                kbChannelRef.current = null;
              }
              if (kbTimeoutRef.current) {
                clearTimeout(kbTimeoutRef.current);
                kbTimeoutRef.current = null;
              }
            }
          }
        )
        .subscribe();
      kbChannelRef.current = channel;
      kbTimeoutRef.current = setTimeout(() => {
        setBuildState((s) => ({ ...s, knowledgeBase: 'done' }));
        if (kbChannelRef.current) {
          supabase.removeChannel(kbChannelRef.current);
          kbChannelRef.current = null;
        }
        kbTimeoutRef.current = null;
      }, 25000);
    } else if (!tenantId) {
      // No tenant_id captured (rare guard-check race) — fall back to the
      // legacy 12s estimate so the UI still flips on time.
      setTimeout(() => {
        setBuildState((s) => ({ ...s, knowledgeBase: 'done' }));
      }, 12000);
    }
    await sleep(600);
    setFoxState('speaking');
    await sleep(500);
    setCompleted(true);
    await sleep(2400);
    // Post-onboarding activation is now driven by the persistent Get-Live
    // Companion + Launch Gate (mounted in Layout.jsx), not a one-shot tour.
    navigate('/dashboard');
  }

  // ─── User submit ────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || !canSend) return;

    if (phoneRetryMode) {
      // The user's reply is the new area code; pull the first 3 digits.
      const digits = text.replace(/\D/g, '');
      const newAreaCode = digits.slice(0, 3);
      setMessages((prev) => [...prev, { role: 'user', text }]);
      setInput('');
      if (newAreaCode.length !== 3) {
        await streamAssistant("That doesn't look like a 3-digit area code, try again?");
        return;
      }
      const nextExtracted = { ...captures, area_code: newAreaCode };
      setCaptures(nextExtracted);
      setPhoneRetryMode(false);
      await streamAssistant(`Trying ${newAreaCode}, hang tight.`);
      await runFinalize(nextExtracted);
      return;
    }

    // Normal flow: append user message and run one chat turn.
    const newHistory = [...messages, { role: 'user', text }];
    setMessages(newHistory);
    setInput('');
    await runChatTurn(newHistory);
  }

  // ─── Admin test review handlers ─────────────────────────────────────────
  // "Write to my tenant" fires the REAL finalize against the admin's own
  // tenant_id (resolved server-side from the JWT). From the point the
  // overlay is dismissed, behavior is identical to a real signup including
  // phone-retry recovery and the dashboard redirect.
  async function handleTestWrite() {
    if (writeInFlightRef.current) return;
    const extracted = pendingTestExtracted;
    if (!extracted) return;
    writeInFlightRef.current = true;
    // Dismiss the overlay so the build animation in the right panel shows.
    setPendingTestExtracted(null);
    try {
      await runFinalize(extracted);
    } finally {
      writeInFlightRef.current = false;
    }
  }

  // "Discard and restart" wipes conversation state and re-seeds with the
  // same hardcoded opener the first run uses. No writes fire, no API call.
  function handleTestDiscard() {
    setMessages([{ role: 'assistant', text: HARDCODED_OPENER, raw: null, streaming: false }]);
    setCaptures({});
    setBuildState({});
    setInput('');
    setPhoneRetryMode(false);
    setIsFoxTyping(false);
    setFoxState('idle');
    setPendingTestExtracted(null);
  }

  return (
    <div className="flex h-screen bg-slate-950 text-slate-100 overflow-hidden">
      <style>{INLINE_STYLES}</style>
      {/* showFox is gated on welcomeDismissed so the LeftPanel fox is hidden
          during the intro. IntroFade slides its own centered fox toward the
          LeftPanel position over beat 3; at handoff (when welcomeDismissed
          flips), the intro unmounts and the LeftPanel fox appears in the
          spot the intro fox just landed in. No double-fox, no flicker. */}
      <LeftPanel foxState={foxState} showFox={welcomeDismissed} />
      <ChatPanel
        messages={messages}
        isFoxTyping={isFoxTyping}
        input={input}
        setInput={setInput}
        onSubmit={handleSubmit}
        canSend={canSend}
        chatScrollRef={chatScrollRef}
        inputRef={inputRef}
        foxState={foxState}
      />
      <NotesPanel captures={captures} buildState={buildState} foxState={foxState} />
      {completed && <CompletionOverlay />}
      {pendingTestExtracted && (
        <AdminTestReviewOverlay
          extracted={pendingTestExtracted}
          onWrite={handleTestWrite}
          onDiscard={handleTestDiscard}
        />
      )}
      {guardChecked && !welcomeDismissed && (
        <IntroFade onStart={() => setWelcomeDismissed(true)} />
      )}
    </div>
  );
}

const INLINE_STYLES = `
@keyframes fox-float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
@keyframes particle-rise { 0% { transform: translateY(0); opacity: 0; } 25% { opacity: 0.75; } 100% { transform: translateY(-140px); opacity: 0; } }
@keyframes inscribe-text { from { clip-path: inset(0 100% 0 0); -webkit-clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); -webkit-clip-path: inset(0 0 0 0); } }
@keyframes notes-line-draw { from { transform: scaleX(0); opacity: 0.9; } to { transform: scaleX(1); opacity: 0.65; } }
@keyframes notes-border-pulse { 0% { opacity: 0.3; } 15% { opacity: 1; } 100% { opacity: 0.3; } }
@keyframes notes-glow-pulse { 0% { opacity: 0; } 20% { opacity: 1; } 100% { opacity: 0; } }
@keyframes thinking-edge { 0%,100% { opacity: 0.35; } 50% { opacity: 0.95; } }
@keyframes overlay-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes glow-pulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.55; } }
.fox-float { animation: fox-float 6s ease-in-out infinite; }
.particle { animation: particle-rise linear infinite; }
.inscribe-text { animation: inscribe-text 700ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.notes-line-draw { animation: notes-line-draw 700ms ease-out forwards; transform-origin: left center; }
.notes-border-pulse { animation: notes-border-pulse 900ms ease-out forwards; }
.notes-glow-pulse { animation: notes-glow-pulse 900ms ease-out forwards; }
.thinking-edge { animation: thinking-edge 2.2s ease-in-out infinite; }
.overlay-in { animation: overlay-in 500ms ease-out forwards; }
.glow-idle { animation: glow-pulse 4s ease-in-out infinite; }
`;

// ─── Left panel ─────────────────────────────────────────────────────────────
// showFox toggles the panel's fox render. During the intro, IntroFade owns
// the visible fox (and slides it toward this panel's position). At handoff
// the intro unmounts and showFox flips true so this panel's fox takes over
// at the same spot. Without this gate, both foxes would be visible at
// different positions during the intro's beat-3 slide, which is what made
// the prior version flicker.
function LeftPanel({ foxState, showFox = true }) {
  return (
    <div className="relative flex-shrink-0 w-[32%] min-w-[320px] h-full bg-slate-950 overflow-hidden border-r border-slate-800/70">
      <HexBackground />
      <Particles />
      <div className="absolute top-6 left-6 flex items-center gap-2 z-10">
        <img src="/logo.png" alt="SurFox AI" className="w-8 h-8 object-contain" />
        <span className="font-semibold tracking-wide text-slate-200">SurFox AI</span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        {showFox && <FoxAsset state={foxState} />}
      </div>
      <div className="absolute bottom-6 left-0 right-0 text-center text-[10px] text-slate-600 tracking-[0.3em] uppercase">
        Surf · your setup guide
      </div>
    </div>
  );
}

function FoxAsset({ state }) {
  // Glow opacity/scale hook into the conversation state. The real fox image
  // sits bottom-anchored in the panel; the radial glow underneath is the
  // floor light at the fox's feet. 'distracted' swaps cyan for amber and
  // adds a tiny head tilt (paused float + rotate(-3deg)) so a chat retry
  // reads as character rather than an error.
  const isThinking = state === 'thinking';
  const isSpeaking = state === 'speaking';
  const isDistracted = state === 'distracted';

  const glowMain = isSpeaking
    ? 'opacity-100 scale-125'
    : isThinking
      ? 'opacity-80 scale-110'
      : isDistracted
        ? 'opacity-65 scale-95'
        : 'opacity-45 scale-100';

  const glowOuter = isDistracted ? 'bg-amber-400' : 'bg-cyan-500';
  const glowInner = isDistracted ? 'bg-amber-300' : 'bg-cyan-300';
  const dropShadow = isDistracted
    ? 'drop-shadow(0 0 12px rgba(251, 191, 36, 0.55))'
    : 'drop-shadow(0 0 12px rgba(0, 200, 180, 0.45))';

  return (
    <div className="relative w-full h-full">
      {/* Floor glow — pinned to the bottom of the panel, beneath the fox's feet */}
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[420px] h-40 rounded-full ${glowOuter} blur-[80px] transition-all duration-700 ease-out pointer-events-none ${glowMain} ${state === 'idle' ? 'glow-idle' : ''}`} />
      <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 w-52 h-24 rounded-full ${glowInner} blur-3xl transition-all duration-500 pointer-events-none ${glowMain}`} />
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-64 h-px bg-gradient-to-r from-transparent via-cyan-300/70 to-transparent pointer-events-none" />

      {/* Full-body fox — bottom-anchored, full panel height */}
      <div className="absolute inset-0 flex justify-center items-end pointer-events-none">
        <img
          src="/full_body_mirrored.png"
          alt="Surf"
          className={`h-auto max-h-full w-auto max-w-full object-contain ${isDistracted ? '' : 'fox-float'}`}
          style={{
            filter: dropShadow,
            transform: isDistracted ? 'rotate(-3deg)' : undefined,
            transition: 'filter 500ms ease, transform 500ms ease',
          }}
        />
      </div>
    </div>
  );
}

function HexBackground() {
  return (
    <svg className="absolute inset-0 w-full h-full text-cyan-500/15 pointer-events-none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="fox-hex-grid" width="48" height="42" patternUnits="userSpaceOnUse">
          <path d="M12 0 L36 0 L48 21 L36 42 L12 42 L0 21 Z" fill="none" stroke="currentColor" strokeWidth="0.6" />
        </pattern>
        <radialGradient id="fox-hex-fade" cx="50%" cy="50%" r="65%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id="fox-hex-mask">
          <rect width="100%" height="100%" fill="url(#fox-hex-fade)" />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="url(#fox-hex-grid)" mask="url(#fox-hex-mask)" />
    </svg>
  );
}

function Particles() {
  const items = Array.from({ length: 18 }, (_, i) => ({
    left: (i * 37) % 100,
    bottom: -10 + ((i * 17) % 30),
    delay: (i * 0.5) % 6,
    dur: 7 + (i % 5),
    size: i % 3 === 0 ? 2 : 1,
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {items.map((p, i) => (
        <div
          key={i}
          className="particle absolute rounded-full bg-cyan-300/60"
          style={{
            left: `${p.left}%`,
            bottom: `${p.bottom}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Center chat ────────────────────────────────────────────────────────────
function ChatPanel({ messages, isFoxTyping, input, setInput, onSubmit, canSend, chatScrollRef, inputRef, foxState }) {
  const isThinking = foxState === 'thinking';
  return (
    <div className="relative flex-1 flex flex-col h-full bg-slate-900/80 min-w-0">
      {/* Top edge — picks up a faint cyan tint during thinking so the screen feels connected */}
      <div
        className={`absolute top-0 left-0 right-0 h-px pointer-events-none transition-colors duration-500 ${
          isThinking ? 'bg-cyan-500/40' : 'bg-transparent'
        }`}
      />
      <div className="relative px-8 py-5 border-b border-slate-800 flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${canSend ? 'bg-cyan-400 animate-pulse' : 'bg-slate-600'}`} />
        <div className="text-sm font-medium text-slate-300">Setup conversation</div>
      </div>
      <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
        {messages.map((m, i) => (
          <ChatMessage key={i} message={m} />
        ))}
        {isFoxTyping && <TypingIndicator />}
      </div>
      <div className="px-8 py-5 border-t border-slate-800">
        <form
          onSubmit={onSubmit}
          className={`flex items-center gap-3 bg-slate-800/60 border rounded-xl px-4 py-2.5 transition-colors ${
            canSend ? 'border-slate-700 focus-within:border-cyan-500/60' : 'border-slate-800 opacity-60'
          }`}
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={canSend ? 'Type your answer...' : 'Surf is thinking...'}
            disabled={!canSend}
            className="flex-1 bg-transparent text-slate-100 placeholder-slate-500 focus:outline-none disabled:cursor-not-allowed"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!canSend || !input.trim()}
            className="p-2 rounded-lg bg-cyan-500 text-slate-950 hover:bg-cyan-400 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  if (message.role === 'assistant') {
    return (
      <div className="flex items-start gap-3 max-w-[88%]">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-xs">S</div>
        <div className="rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-4 py-3 text-slate-100 leading-relaxed">
          <span>{message.text}</span>
          {message.streaming && (
            <span className="inline-block w-1.5 h-4 bg-cyan-400 ml-1 align-middle animate-pulse" />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div className="rounded-2xl rounded-tr-sm bg-cyan-500/15 border border-cyan-500/30 px-4 py-3 text-slate-100 max-w-[80%] leading-relaxed">
        {message.text}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-xs">S</div>
      <div className="rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-4 py-3">
        <div className="flex gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '160ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: '320ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Right notes panel ──────────────────────────────────────────────────────
function NotesPanel({ captures, buildState, foxState }) {
  // Bump pulseKey whenever a new value lands. The remounted overlay nodes
  // restart their CSS animations, giving us a one-shot pulse per capture.
  const [pulseKey, setPulseKey] = useState(0);
  const prevCountRef = useRef(0);
  const isThinking = foxState === 'thinking';

  useEffect(() => {
    const count = Object.keys(captures).length;
    if (count > prevCountRef.current) setPulseKey((k) => k + 1);
    prevCountRef.current = count;
  }, [captures]);

  return (
    <div className="relative flex-shrink-0 w-[26%] min-w-[280px] h-full bg-slate-950 flex flex-col">
      {/* Permanent cyan left edge — separates the panel from the chat */}
      <div className="absolute inset-y-0 left-0 w-px bg-cyan-500/25 pointer-events-none z-10" />
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-cyan-500/[0.04] to-transparent pointer-events-none" />

      {/* Capture pulse — bright line + inward glow bleed, retriggered by pulseKey */}
      {pulseKey > 0 && (
        <>
          <div
            key={`pulse-line-${pulseKey}`}
            className="absolute inset-y-0 left-0 w-px bg-cyan-300 notes-border-pulse pointer-events-none z-20"
          />
          <div
            key={`pulse-glow-${pulseKey}`}
            className="absolute inset-y-0 left-0 w-32 notes-glow-pulse pointer-events-none"
            style={{ background: 'linear-gradient(to right, rgba(34,211,238,0.35), transparent)' }}
          />
        </>
      )}

      {/* Thinking-state right edge — syncs with the fox glow to tie the screen together */}
      {isThinking && (
        <>
          <div className="absolute inset-y-0 right-0 w-px bg-cyan-500/50 thinking-edge pointer-events-none z-10" />
          <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-cyan-500/[0.07] to-transparent thinking-edge pointer-events-none" />
        </>
      )}

      <div className="relative px-6 py-5 border-b border-slate-800">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Notes</div>
        <div className="text-sm text-slate-400">What I've learned so far</div>
      </div>
      <div className="relative flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {NOTES_FIELDS.map((field) => (
          <NoteItem key={field.key} label={field.label} value={captures[field.key]} />
        ))}
      </div>
      <div className="relative px-6 py-5 border-t border-slate-800 bg-slate-900/40">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Building</div>
        <div className="space-y-2.5">
          {BUILD_TASKS.map((task) => (
            <BuildItem key={task.key} label={task.label} state={buildState[task.key] || 'idle'} />
          ))}
        </div>
      </div>
    </div>
  );
}

function NoteItem({ label, value }) {
  const captured = !!value;
  return (
    <div className="relative">
      <div className="flex items-start gap-3">
        <div
          className={`flex-shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300 ${
            captured
              ? 'bg-cyan-500 shadow-md shadow-cyan-500/30'
              : 'bg-slate-800 border border-slate-700 animate-pulse'
          }`}
        >
          {captured && <Check size={10} className="text-slate-950" strokeWidth={3} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
          {captured ? (
            <div
              key={value}
              className="inscribe-text text-base font-medium text-white mt-1.5 break-words leading-snug"
            >
              {value}
            </div>
          ) : (
            <div className="text-sm text-slate-700 mt-1">—</div>
          )}
        </div>
      </div>
      {/* Full-panel-width hairline — extends past parent padding (px-6) and draws left-to-right on capture */}
      {captured && (
        <div
          key={`line-${value}`}
          className="absolute -left-6 -right-6 -bottom-2.5 h-px notes-line-draw pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, rgba(34,211,238,0) 0%, rgba(34,211,238,0.75) 50%, rgba(34,211,238,0) 100%)',
          }}
        />
      )}
    </div>
  );
}

function BuildItem({ label, state }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
        {state === 'done' ? (
          <div className="w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
            <Check size={10} className="text-slate-950" strokeWidth={3} />
          </div>
        ) : state === 'in_progress' ? (
          <Loader2 size={14} className="text-cyan-400 animate-spin" />
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-slate-700" />
        )}
      </div>
      <div
        className={`text-sm transition-colors ${
          state === 'done'
            ? 'text-slate-200'
            : state === 'in_progress'
              ? 'text-cyan-300'
              : 'text-slate-500'
        }`}
      >
        {label}
      </div>
    </div>
  );
}

// ─── Completion overlay ─────────────────────────────────────────────────────
function CompletionOverlay({
  title = "You're set up.",
  subtitle = 'Opening your dashboard...',
}) {
  return (
    <div className="overlay-in fixed inset-0 z-50 bg-slate-950/92 backdrop-blur-md flex items-center justify-center">
      <div className="text-center">
        <div className="relative inline-block">
          <div className="absolute inset-0 rounded-full bg-cyan-400 blur-3xl opacity-70 animate-pulse" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-cyan-300 to-blue-600 flex items-center justify-center shadow-2xl shadow-cyan-500/50">
            <Check size={40} className="text-slate-950" strokeWidth={3} />
          </div>
        </div>
        <div className="mt-8 text-2xl font-semibold text-slate-100">{title}</div>
        <div className="mt-2 text-slate-400">{subtitle}</div>
      </div>
    </div>
  );
}

// Admin-only review panel shown when complete=true fires in testMode.
// Shows a readable summary of the captured values and lets the admin choose
// between firing the real finalize or discarding the run.
function AdminTestReviewOverlay({ extracted, onWrite, onDiscard }) {
  const rows = [
    { label: 'Company',              value: extracted?.company_name },
    { label: 'Industry',             value: extracted?.industry },
    { label: 'Talk track type',      value: extracted?.talk_track_type },
    { label: 'Talk track specialty', value: extracted?.talk_track_specialty },
    { label: 'Tone',                 value: extracted?.tone },
    { label: 'Persona',              value: extracted?.persona },
    { label: 'Area code',            value: extracted?.area_code },
  ];

  return (
    <div className="overlay-in fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-md flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700/80 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-2">Admin test review</div>
          <div className="text-2xl font-semibold text-slate-100">Ready to commit?</div>
          <div className="text-xs text-slate-500 mt-2 leading-relaxed">
            Writing will fire the real finalize against your tenant. A real Twilio number will be purchased.
          </div>
        </div>

        <div className="space-y-3 mb-7">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-32 text-[10px] uppercase tracking-widest text-slate-500 pt-1">
                {row.label}
              </div>
              <div className="flex-1 text-sm text-slate-100 font-medium break-words">
                {row.value || <span className="text-slate-600 italic font-normal">not extracted</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={onWrite}
            className="w-full px-4 py-2.5 rounded-lg font-medium text-sm bg-cyan-500 text-slate-950 hover:bg-cyan-400 transition-colors"
          >
            Write to my tenant
          </button>
          <button
            onClick={onDiscard}
            className="w-full px-4 py-2.5 rounded-lg font-medium text-sm bg-transparent border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
          >
            Discard and restart
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Intro fade sequence ────────────────────────────────────────────────────
// Three beats + a slide-handoff. "Welcome in." → "Setting up your AI." →
// "Meet Surf." with the fox materializing centered, then on beat 3 the
// backdrop dissolves and the fox SLIDES from center to the LeftPanel
// position. At handoff time (onStart fires) the intro unmounts and the
// LeftPanel's own fox takes over at the same screen spot. The earlier
// version cross-faded two foxes at different positions (and one was the
// mirrored asset, so the character flipped direction) — that's the
// flicker. This version uses the mirrored PNG throughout so the
// direction stays constant, and the position match makes the unmount
// feel like a settle, not a jump.
//
// Position constants below assume the LeftPanel is 32vw wide on the left.
// LeftPanel center = 16vw; viewport center = 50vw; so the slide is -34vw.
// translateY(80px) drops the fox 80px to remove the intro's pb-20 padding
// and bottom-align with the LeftPanel fox. scale(1.25) bumps the intro fox
// up roughly to LeftPanel-fox size. If the final position is off by a few
// pixels, nudge these three values in DevTools and bake them back here.
function IntroFade({ onStart }) {
  const [beat, setBeat] = useState(0); // 0=welcome 1=setup 2=meet 3=handoff
  useEffect(() => {
    const timers = [
      setTimeout(() => setBeat(1), 1300),
      setTimeout(() => setBeat(2), 2500),
      setTimeout(() => setBeat(3), 4000),
      setTimeout(() => onStart(), 5600),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onStart]);

  const backdropVisible = beat < 3;
  const foxTransform = beat >= 3
    ? 'translateX(-34vw) translateY(80px) scale(1.25)'
    : 'translateX(0) translateY(0) scale(1)';

  return (
    <div className={`fixed inset-0 z-50 ${beat >= 3 ? 'pointer-events-none' : ''}`}>
      {/* Backdrop layer — dark + atmosphere + text. Fades on beat 3. The
          fox is OUTSIDE this layer so it persists through the dissolve. */}
      <div
        className={`absolute inset-0 bg-slate-950 transition-opacity duration-[1200ms] ease-out ${
          backdropVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <HexBackground />
        <Particles />

        {/* Floor glow grows in at beat 1, anchors the fox arrival on beat 2 */}
        <div
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-48 rounded-full bg-cyan-500 blur-[100px] pointer-events-none transition-all duration-[1400ms] ease-out ${
            beat === 0 ? 'opacity-0 scale-50' : 'opacity-70 scale-100'
          }`}
        />

        {/* Beat 1 — Welcome in. */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
            beat === 0 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-4xl font-light text-slate-100 tracking-wide">Welcome in.</div>
        </div>

        {/* Beat 2 — Setting up your AI. */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
            beat === 1 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-3xl font-light text-slate-200 tracking-wide">Setting up your AI.</div>
        </div>

        {/* Beat 3 — Meet Surf. tagline (rendered low so it doesn't fight
            the fox image for vertical real estate). Fades out with the
            backdrop on beat 3. */}
        <div
          className={`absolute inset-x-0 bottom-16 flex justify-center transition-opacity duration-500 ${
            beat === 2 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-3xl font-light text-slate-100 tracking-wide">Meet Surf.</div>
        </div>
      </div>

      {/* Fox layer — outside the backdrop. Mirrored PNG (same as LeftPanel)
          so the character never flips direction across the handoff.
          Structure note: the transform lives on an INNER wrapper around
          the img (not the outer flex container) and transform-origin is
          pinned to "center bottom". That way scale(1.25) on beat 3 only
          extends the fox upward — his feet stay anchored. The outer
          container keeps pb-20 so the centered fox in beats 0-2 has room
          for the "Meet Surf" tagline below; translateY(80px) on beat 3
          drops the feet from `viewport_bottom - 80px` to `viewport_bottom`
          to match where LeftPanel renders its fox. fox-float stays on the
          img (separate transform space) so the bob is preserved. */}
      <div className="absolute inset-0 flex justify-center items-end pb-20 pointer-events-none">
        <div
          style={{
            transform: foxTransform,
            transformOrigin: 'center bottom',
            opacity: beat >= 2 ? 1 : 0,
            transition: 'transform 1500ms cubic-bezier(0.4, 0.0, 0.2, 1), opacity 600ms ease-out',
          }}
        >
          <img
            src="/full_body_mirrored.png"
            alt="Surf"
            className="h-[58vh] max-h-[480px] w-auto object-contain fox-float"
            style={{ filter: 'drop-shadow(0 0 24px rgba(0, 200, 180, 0.55))' }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Preview badge ──────────────────────────────────────────────────────────
function PreviewBadge() {
  return (
    <div className="fixed bottom-3 right-3 text-[10px] uppercase tracking-widest text-slate-600 bg-slate-900/80 border border-slate-800 rounded-md px-2 py-1 pointer-events-none">
      preview · /onboarding-v2
    </div>
  );
}
